import React from 'react';
import { useLocation } from 'react-router';
import { Menu, Mail, Phone, ChevronRight } from 'lucide-react';
import {
  Button,
  Github,
  Link,
  LinkButton,
  Linkedin,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
  ThemeToggle,
} from '@/shared/ui';
import { useProfile, useSiteContent } from '@personal-portfolio/queries';
import { cn } from '@/shared/lib/cn';
import { navigationItems } from './navigation';
import { ResumeCard } from '@/features/resume-download/resume-card';

// Elevation threshold: the header gains a border/shadow once the page has
// scrolled past this many pixels.
const SCROLL_THRESHOLD = 8;

const useHeaderScrolled = () => {
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    let frame = 0;
    const update = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    const onScroll = () => {
      // rAF-throttle: coalesce scroll events into one update per frame.
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return scrolled;
};

export const Header: React.FC = () => {
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);
  const { pathname } = useLocation();
  const scrolled = useHeaderScrolled();
  const { data: profileData } = useProfile();
  const content = useSiteContent();
  const siteName = profileData?.name || content.site.name;

  const navLabels = navigationItems.map((item) => content.nav[item.titleKey]);
  const navDescription = `Main navigation menu for portfolio sections including ${navLabels
    .slice(0, -1)
    .join(', ')}, and ${navLabels[navLabels.length - 1]}.`;

  // Case-insensitive: `type` is free-text CMS content, so its casing is not
  // guaranteed to match the literals used at the call sites below.
  const getSocialLink = (type: string) => {
    return profileData?.socialLinks?.find(
      (link) => link.type?.toLowerCase() === type.toLowerCase(),
    );
  };

  const linkedinLink = getSocialLink('linkedin');
  const githubLink = getSocialLink('github');

  return (
    <header
      data-scrolled={scrolled || undefined}
      className={cn(
        'fixed top-0 z-50 w-full border-b backdrop-blur-md transition-[border-color,box-shadow,background-color] duration-200',
        scrolled
          ? 'border-border/50 bg-background/80 shadow-xs'
          : 'border-transparent',
      )}
    >
      <div className="flex h-14 items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <SheetTrigger isOpen={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <Button variant="ghost" size="icon" className="lg:hidden">
              <Menu className="h-4 w-4" />
              <span className="sr-only">Toggle menu</span>
            </Button>
            <SheetContent side="left" className="w-64">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <SheetDescription className="sr-only">
                {navDescription}
              </SheetDescription>
              <div className="flex h-full flex-col">
                <div className="flex-1 space-y-4 py-4">
                  <div className="px-3 py-2">
                    <h2 className="mb-3 px-4 font-mono text-xs font-medium tracking-widest text-muted-foreground uppercase">
                      {content.chrome.brand}
                    </h2>
                    <div className="space-y-1">
                      {navigationItems.map((item) => {
                        const isActive = pathname === item.path;
                        return (
                          <LinkButton
                            key={item.path}
                            href={item.path}
                            variant={isActive ? 'secondary' : 'ghost'}
                            onPress={() => setIsSheetOpen(false)}
                            className={cn(
                              'w-full justify-start transition-colors duration-150',
                              isActive
                                ? 'font-medium'
                                : 'text-muted-foreground hover:text-foreground',
                            )}
                          >
                            <item.icon className="mr-2 h-4 w-4" />
                            {content.nav[item.titleKey]}
                            {isActive && (
                              <ChevronRight className="ml-auto h-4 w-4" />
                            )}
                          </LinkButton>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Get Resume Card (mobile sheet) */}
                <div className="px-6">
                  <ResumeCard />
                </div>

                {/* Made by credit with icon - Fixed at bottom */}
                <div className="p-6">
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground">
                      {content.chrome.madeBy}
                    </div>
                    <div className="flex items-center space-x-2">
                      <img
                        src="/favicon-96x96.png"
                        alt={siteName}
                        className="h-6 w-6 rounded-sm"
                      />
                      <span className="text-m font-medium text-foreground">
                        {siteName}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </SheetContent>
          </SheetTrigger>
          <Link href="/" className="flex items-center">
            {/* Text wordmark - hidden on mobile (the sheet carries the brand) */}
            <span className="hidden text-sm font-semibold tracking-tight text-foreground lg:block">
              {siteName}
            </span>
          </Link>
        </div>

        <nav aria-label="Contact links" className="flex items-center space-x-2">
          {profileData?.phone && (
            <LinkButton
              variant="ghost"
              size="icon"
              href={`tel:${profileData.phone}`}
            >
              <Phone className="h-4 w-4" />
              <span className="sr-only">Call</span>
            </LinkButton>
          )}
          {profileData?.email && (
            <LinkButton
              variant="ghost"
              size="icon"
              href={`mailto:${profileData.email}`}
            >
              <Mail className="h-4 w-4" />
              <span className="sr-only">Email</span>
            </LinkButton>
          )}
          {linkedinLink && (
            <LinkButton
              variant="ghost"
              size="icon"
              href={linkedinLink.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Linkedin className="h-4 w-4" />
              <span className="sr-only">LinkedIn</span>
            </LinkButton>
          )}
          {githubLink && (
            <LinkButton
              variant="ghost"
              size="icon"
              href={githubLink.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Github className="h-4 w-4" />
              <span className="sr-only">GitHub</span>
            </LinkButton>
          )}
          <ThemeToggle />
        </nav>
      </div>
    </header>
  );
};
